import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Typography,
} from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import React, { useEffect, useState } from 'react';
import { GeolocationStatus } from 'common/hooks/use-geolocation';

interface IRequireLocationGateProps {
    status: GeolocationStatus;
    error: string;
    onRequest: () => void;
    // Bật khi khảo sát đã sẵn sàng trả lời. Trước đó (vd: màn nhập mã) không nhắc
    // gì cả, để popup vị trí không đè lên dialog nhập mã - user gạt popup vài lần
    // là Chrome tự chuyển sang chặn cứng, khoá luôn người trả lời hợp lệ.
    enabled: boolean;
    children: React.ReactNode;
}

// Nhắc người trả lời cấp quyền vị trí trước khi vào khảo sát.
// Chốt chặn thật nằm ở nút "Bắt đầu khảo sát" (disabled) chứ không phải ở dialog:
// dialog phải đóng được, nếu không creator vào preview mà từ chối vị trí sẽ bị
// backdrop chặn hết, không bấm được nút thoát về màn thiết kế.
const RequireLocationGate: React.FC<IRequireLocationGateProps> = ({
    status,
    error,
    onRequest,
    enabled,
    children,
}) => {
    const [dismissed, setDismissed] = useState(false);
    const isWaiting = status === 'idle' || status === 'prompting';
    const needsLocation = enabled && status !== 'granted';

    // Chỉ hiện nút khi bấm vào đổi được trạng thái thật: 'dismissed' thì gọi lại
    // được popup, 'unavailable' thì thử lấy toạ độ lại. Ở 'blocked' không lệnh JS
    // nào mở lại được popup nên không đưa nút - hook tự nhận khi user bật lại quyền.
    const actionLabel = (() => {
        if (status === 'dismissed') return 'Cho phép truy cập vị trí';
        if (status === 'unavailable') return 'Thử lại';
        return '';
    })();

    // Đang xin quyền lại (vd: hook tự nhận ra user vừa bật quyền) thì hiện lại
    // dialog để người dùng thấy tiến trình thay vì im lặng.
    useEffect(() => {
        if (isWaiting) {
            setDismissed(false);
        }
    }, [isWaiting]);

    return (
        <>
            {children}

            <Dialog
                open={needsLocation && !dismissed}
                onClose={() => setDismissed(true)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                    <LocationOnOutlinedIcon color="primary" />
                    Cần quyền truy cập vị trí
                </DialogTitle>
                <DialogContent>
                    {isWaiting ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                py: 1,
                            }}
                        >
                            <CircularProgress size={24} />
                            <Typography variant="body1">
                                Đang xác định vị trí của bạn...
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                {error ||
                                    'Khảo sát này yêu cầu chia sẻ vị trí để có thể tham gia trả lời.'}
                            </Typography>
                            {(status === 'blocked' ||
                                status === 'unavailable') && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Vui lòng kiểm tra lại quyền vị trí trong cài
                                    đặt trình duyệt và bật định vị trên thiết bị
                                    của bạn.
                                </Typography>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDismissed(true)}>Đóng</Button>
                    {actionLabel && (
                        <Button variant="contained" onClick={onRequest}>
                            {actionLabel}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Đóng dialog rồi thì vẫn phải còn đường quay lại, nếu không người
                dùng chỉ thấy nút "Bắt đầu khảo sát" xám mà không hiểu vì sao. */}
            {needsLocation && dismissed && (
                <Paper
                    elevation={6}
                    sx={{
                        position: 'fixed',
                        bottom: 16,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: (theme) => theme.zIndex.snackbar,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        maxWidth: 'calc(100vw - 32px)',
                    }}
                >
                    <LocationOnOutlinedIcon color="primary" />
                    <Typography variant="body2">
                        Cần bật vị trí để tham gia trả lời khảo sát.
                    </Typography>
                    <Button
                        size="small"
                        variant="contained"
                        onClick={() => setDismissed(false)}
                    >
                        Bật vị trí
                    </Button>
                </Paper>
            )}
        </>
    );
};

export default RequireLocationGate;
